/**
 * Wallet picker — OpenPlaza layout, SportsFuture light chrome.
 * Music / KeepCell are not part of this modal.
 */

import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, ExternalLink, Wifi, Smartphone } from 'lucide-react';
import type { DiscoveredWallet } from '../../hooks/useWalletDiscovery';
import type { MobileWalletInfo } from '../../utils/mobileWalletDetect';

interface WalletSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  wallets: DiscoveredWallet[];
  isDiscovering: boolean;
  onSelectWallet: (wallet: DiscoveredWallet) => void;
  isConnecting: boolean;
  connectingWalletId?: string | null;
  mobileWalletInfo?: MobileWalletInfo | null;
}

const ShimmerRow = () => (
  <div className="flex w-full animate-pulse items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
    <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-slate-200" />
    <div className="h-4 w-28 rounded bg-slate-200" />
  </div>
);

const WalletSelectionModal = ({
  isOpen,
  onClose,
  wallets,
  isDiscovering,
  onSelectWallet,
  isConnecting,
  connectingWalletId = null,
  mobileWalletInfo = null,
}: WalletSelectionModalProps) => {
  const handleSelect = useCallback(
    (wallet: DiscoveredWallet) => {
      if (isConnecting) return;
      onSelectWallet(wallet);
    },
    [isConnecting, onSelectWallet]
  );

  if (!isOpen || typeof document === 'undefined') return null;

  const isMobile = mobileWalletInfo?.isMobile ?? false;
  const isMobileWalletBrowser = mobileWalletInfo?.isMobileWalletBrowser ?? false;
  const detectedWalletName = mobileWalletInfo?.detectedWalletName ?? null;
  const injectedWallets = wallets.filter((w) => w.type === 'injected');
  const wcWallet = wallets.find((w) => w.type === 'walletconnect');

  const discoveryMessage = isMobileWalletBrowser && detectedWalletName
    ? `Detecting ${detectedWalletName}...`
    : isMobile
      ? 'Detecting wallet...'
      : 'Detecting wallets...';

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Connect wallet"
    >
      <div className="flex w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-5">
          <h2 className="text-lg font-bold text-slate-900">Connect Wallet</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {isDiscovering && (
            <div className="mb-4">
              <div className="mb-3 flex items-center gap-3 text-sm text-slate-500">
                <Loader2 size={16} className="animate-spin text-primary" />
                {discoveryMessage}
              </div>
              {isMobile && injectedWallets.length === 0 && (
                <div className="space-y-2">
                  <ShimmerRow />
                  {!isMobileWalletBrowser && <ShimmerRow />}
                </div>
              )}
            </div>
          )}

          {injectedWallets.length > 0 && (
            <div className="mb-4">
              <p className="mb-3 px-1 text-[10px] uppercase tracking-wider text-slate-400">
                {isMobileWalletBrowser ? 'Wallet Detected' : 'Detected Wallets'}
              </p>
              <div className="space-y-2">
                {injectedWallets.map((wallet) => {
                  const busy = isConnecting && connectingWalletId === wallet.id;
                  return (
                    <button
                      key={wallet.id}
                      type="button"
                      onClick={() => handleSelect(wallet)}
                      disabled={isConnecting}
                      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                        busy
                          ? 'border-primary/50 bg-primary-50'
                          : 'border-slate-200 bg-white hover:border-primary/40 hover:bg-primary-50'
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                        {wallet.icon ? (
                          <img
                            src={wallet.icon}
                            alt=""
                            className="h-8 w-8 object-contain"
                          />
                        ) : (
                          <span className="text-sm font-semibold text-slate-400">
                            {wallet.name.slice(0, 1)}
                          </span>
                        )}
                      </div>
                      <span className="flex-1 text-sm font-medium text-slate-900">
                        {wallet.name}
                      </span>
                      {busy ? (
                        <Loader2 size={16} className="flex-shrink-0 animate-spin text-primary" />
                      ) : (
                        <div className="h-2 w-2 flex-shrink-0 rounded-full bg-green-500/70" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!isDiscovering && injectedWallets.length === 0 && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center">
              {isMobile ? (
                <>
                  <Smartphone size={20} className="mx-auto mb-2 text-slate-400" />
                  <p className="mb-2 text-sm text-slate-600">
                    {isMobileWalletBrowser
                      ? 'Wallet provider not detected'
                      : 'No mobile wallet detected'}
                  </p>
                  <p className="text-xs text-slate-500">
                    Open this page in your wallet app&apos;s browser, or install{' '}
                    <a
                      href="https://metamask.io/download/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      MetaMask <ExternalLink size={10} />
                    </a>
                    .
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-2 text-sm text-slate-600">No browser wallets detected</p>
                  <p className="text-xs text-slate-500">
                    Install{' '}
                    <a
                      href="https://metamask.io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      MetaMask <ExternalLink size={10} />
                    </a>{' '}
                    or another EVM-compatible browser wallet
                  </p>
                </>
              )}
            </div>
          )}

          {injectedWallets.length > 0 && wcWallet && (
            <div className="my-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[10px] uppercase tracking-wider text-slate-400">or</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
          )}

          {wcWallet && (
            <div>
              <p className="mb-3 px-1 text-[10px] uppercase tracking-wider text-slate-400">
                Mobile & External Wallets
              </p>
              <button
                type="button"
                onClick={() => handleSelect(wcWallet)}
                disabled={isConnecting}
                className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                  isConnecting && connectingWalletId === 'walletconnect'
                    ? 'border-[#3B99FC]/50 bg-[#3B99FC]/5'
                    : 'border-slate-200 bg-white hover:border-[#3B99FC]/40 hover:bg-slate-50'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#3B99FC]/10">
                  <img src={wcWallet.icon} alt="" className="h-7 w-7 object-contain" />
                </div>
                <div className="flex-1">
                  <span className="block text-sm font-medium text-slate-900">WalletConnect</span>
                  <span className="text-[11px] text-slate-500">Scan with your mobile wallet</span>
                </div>
                {isConnecting && connectingWalletId === 'walletconnect' ? (
                  <Loader2 size={16} className="flex-shrink-0 animate-spin text-[#3B99FC]" />
                ) : (
                  <Wifi size={16} className="flex-shrink-0 text-slate-400" />
                )}
              </button>
            </div>
          )}
        </div>

        <div className="px-5 pb-5 pt-2">
          <p className="text-center text-[10px] leading-relaxed text-slate-400">
            By connecting, you agree to SportsFuture&apos;s{' '}
            <a href="/legal" className="text-primary underline hover:text-primary-600">
              Terms of Service
            </a>
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WalletSelectionModal;
